import React, { useState, useEffect, useRef } from 'react';
import { Card, Typography, Button, Space, Upload, Image, message, Row, Col, Progress, Checkbox, Modal, Collapse } from 'antd';
import { UploadOutlined, LeftOutlined, RightOutlined, CloseOutlined } from '@ant-design/icons';
import type { UploadFile, UploadChangeParam } from 'antd/es/upload/interface';
import type { CheckboxChangeEvent } from 'antd/es/checkbox';
import { useSwipeable } from 'react-swipeable';
import { authPpiApiAxiosWithRefresh } from '../../services/api';

const { Title, Text, Paragraph } = Typography;

interface ImageReviewProps {
  initialImages: string[];
  onSubmit: (images: string[], skipAnalysis: boolean) => void;
  onBack: () => void;
  docId?: string;
  onUploadSuccess: () => void;
  isManual: boolean;
}

export const ImageReview: React.FC<ImageReviewProps> = ({
  initialImages,
  onSubmit,
  onBack,
  docId,
  onUploadSuccess,
  isManual
}) => {
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [thumbnailOffset, setThumbnailOffset] = useState(0);
  const [skipImageAnalysis, setSkipImageAnalysis] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isLoadingStatus, setIsLoadingStatus] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [images, setImages] = useState<string[]>(initialImages);
  const [deleting, setDeleting] = useState(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [imageToDeleteUrl, setImageToDeleteUrl] = useState<string | null>(null);
  const [dontShowDeleteModalAgain, setDontShowDeleteModalAgain] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    setImages(initialImages);
  }, [initialImages]);

  useEffect(() => {
    const dontShow = localStorage.getItem('dontShowDeleteModalAgain') === 'true';
    setDontShowDeleteModalAgain(dontShow);
  }, []);

  const executeDelete = async (imageUrl: string) => {
    if (!docId || deleting) return;
    setDeleting(true);
    try {
      await (await authPpiApiAxiosWithRefresh()).delete(`/delete-image/${docId}`, {
        data: { image_url: imageUrl },
      });
      message.success('Image deleted successfully');
      setImages(prevImages => prevImages.filter(img => img !== imageUrl));
    } catch (err) {
      message.error('Failed to delete image. Please try again.');
    } finally {
      setDeleting(false);
      setIsDeleteModalVisible(false);
    }
  };

  const handleDeleteClick = (imageUrl: string) => {
    if (dontShowDeleteModalAgain) {
      executeDelete(imageUrl);
    } else {
      setImageToDeleteUrl(imageUrl);
      setIsDeleteModalVisible(true);
    }
  };

  const thumbnailContainerRef = useRef<HTMLDivElement>(null);
  const thumbnailTrackRef = useRef<HTMLDivElement>(null);
  const thumbnailRefs = useRef<(HTMLDivElement | null)[]>([]);



  const updateSkipStatus = async (shouldSkip: boolean) => {
    if (!docId) return;
    setIsUpdating(true);
    try {
      await (await authPpiApiAxiosWithRefresh()).post(`/skip-image-analysis/${docId}`, {
        skip: shouldSkip,
      });
    } catch (err) {
      message.error('Failed to update status. Please try again.');
      // Revert the optimistic state change on failure
      setSkipImageAnalysis(!shouldSkip);
    } finally {
      setIsUpdating(false);
    }
  };

  // Effect to fetch the initial skip status when the component loads
  useEffect(() => {
    const fetchSkipStatus = async () => {
      if (!docId) {
        setIsLoadingStatus(false);
        return;
      }
      try {
        const response = await (await authPpiApiAxiosWithRefresh()).get(`/analysis-status/${docId}`);
        const imageStatus = response.data?.steps?.image_analysis;
        setSkipImageAnalysis(imageStatus === 'skipped');
      } catch (err) {
        console.error("Could not fetch image analysis status", err);
        setSkipImageAnalysis(false);
      } finally {
        setIsLoadingStatus(false);
      }
    };

    fetchSkipStatus();
  }, [docId]);

  // Effect to adjust thumbnail carousel when index changes
  useEffect(() => {
    if (thumbnailContainerRef.current && thumbnailTrackRef.current && thumbnailRefs.current[currentImageIndex]) {
      const container = thumbnailContainerRef.current;
      const track = thumbnailTrackRef.current;
      const activeThumbnail = thumbnailRefs.current[currentImageIndex]!;

      const containerWidth = container.offsetWidth;
      const trackWidth = track.scrollWidth;
      const activeThumbnailWidth = activeThumbnail.offsetWidth;
      const activeThumbnailOffsetLeft = activeThumbnail.offsetLeft;

      // Center the active thumbnail in the container
      let newOffset = (containerWidth / 2) - activeThumbnailOffsetLeft - (activeThumbnailWidth / 2);

      // Clamp the offset to prevent showing empty space on either side
      const maxOffset = 0;
      const minOffset = containerWidth - trackWidth;

      if (trackWidth < containerWidth) {
        newOffset = 0;
      } else {
        newOffset = Math.max(minOffset, Math.min(maxOffset, newOffset));
      }
      
      setThumbnailOffset(newOffset);
    }
  }, [currentImageIndex, images.length]);

  useEffect(() => {
    if (currentImageIndex >= images.length && images.length > 0) {
      setCurrentImageIndex(images.length - 1);
    } else if (images.length === 0) {
      setCurrentImageIndex(0);
    }

    
  }, [images, images.length, currentImageIndex]);

  const handleFileChange = (info: UploadChangeParam<UploadFile>) => {
    setFileList(info.fileList);
  };

  const handleBatchUpload = async () => {
    const filesToUpload = fileList.filter(file => file.originFileObj);
    const totalFiles = filesToUpload.length;

    if (!docId) {
      message.error('Document ID is required for upload');
      return;
    }
    
    if (totalFiles === 0) {
      message.warning('No files selected for upload');
      return;
    }
    
    setUploading(true);
    setUploadProgress(0);
    
    let successCount = 0;

    for (let i = 0; i < totalFiles; i++) {
      const file = filesToUpload[i];
      const formData = new FormData();
      formData.append('files', file.originFileObj as File);

      try {
        const response = await (await authPpiApiAxiosWithRefresh()).post(`/upload-images/${docId}`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          }
        });

        if (response.data.success) {
          successCount++;
        } else {
          message.error(`Failed to upload ${file.name}: ${response.data.error || 'Upload failed'}`);
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Network error';
        message.error(`Failed to upload ${file.name}: ${errorMessage}`);
      }
      
      // Update overall progress after each file
      setUploadProgress(Math.round(((i + 1) / totalFiles) * 100));
    }
    
    setUploading(false);

    if (successCount > 0) {
      message.success(`Uploaded ${successCount} of ${totalFiles} image${totalFiles > 1 ? 's' : ''}.`);
      onUploadSuccess(); // Refresh the image list from the server
    }

    // Clear the list of selected files after the upload attempt is complete
    setFileList([]);
  };

  const handleSubmit = () => {
    if (fileList.length > 0) {
      message.warning('You have selected images that are not uploaded. Please upload them or remove them before continuing.');
      return;
    }
    onSubmit(initialImages, skipImageAnalysis);
  };

  const handlePrevious = () => {
    setCurrentImageIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1));
  };

  const handleNext = () => {
    setCurrentImageIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0));
  };

  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => handleNext(),
    onSwipedRight: () => handlePrevious(),
    preventScrollOnSwipe: true,
    trackMouse: true
  });

  const handleThumbnailClick = (index: number) => {
    setCurrentImageIndex(index);
  };

  const handleSkipAnalysisChange = (e: CheckboxChangeEvent) => {
    if (isUpdating || isLoadingStatus) return; // Prevent changes while an update is in progress

    const isChecked = e.target.checked;
    setSkipImageAnalysis(isChecked); // Optimistically update the UI

    if (isChecked) {
      // If checking the box, open the modal for confirmation
      setIsModalVisible(true);
    } else {
      // If unchecking, call the API immediately
      updateSkipStatus(false);
    }
  };

  const handleModalOk = () => {
    setIsModalVisible(false);
    // The checkbox is already checked from the optimistic update, so just call the API
    updateSkipStatus(true);
  };

  const handleModalCancel = () => {
    // If the user cancels, revert the optimistic UI change
    setSkipImageAnalysis(false);
    setIsModalVisible(false);
  };

  return (
    <Card>
      <Title level={4}>{isManual ? 'Add Images' : 'Review & Add Photos to Analyze'}</Title>
      <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
      Our AI reviews each image for signs of wear, paint defects, or mechanical issues. Images are added automatically from the auction listing.
      </Text>

      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {images.length > 0 && (
          <Card title={`Image ${currentImageIndex + 1} of ${images.length}`}>
            {/* Main Image Carousel */}
            <Row justify="center" align="middle" gutter={16} style={{ marginBottom: '24px' }}>
              {!isMobile && (
                <Col flex="none">
                  <Button icon={<LeftOutlined />} onClick={handlePrevious} disabled={images.length <= 1} />
                </Col>
              )}
              <Col flex="auto" style={{ textAlign: 'center', position: 'relative' }} {...(isMobile ? swipeHandlers : {})}>
                <Button
                  icon={<CloseOutlined />}
                  onClick={() => handleDeleteClick(images[currentImageIndex])}
                  style={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    zIndex: 10,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    color: 'white',
                    border: 'none'
                  }}
                  shape="circle"
                  danger
                  loading={deleting}
                />
                <Image
                  width="100%"
                  style={{ maxHeight: 400, objectFit: 'contain', borderRadius: '8px' }}
                  src={images[currentImageIndex]}
                  alt={`Vehicle image ${currentImageIndex + 1}`}
                  preview={false}
                />
              </Col>
              {!isMobile && (
                <Col flex="none">
                  <Button icon={<RightOutlined />} onClick={handleNext} disabled={images.length <= 1} />
                </Col>
              )}
            </Row>

            {/* Thumbnails */}
            {images.length > 1 && (
              <Row justify="center">
                <Col style={{ width: '100%', maxWidth: '500px' }}>
                  <div ref={thumbnailContainerRef} style={{ overflow: 'hidden' }}>
                    <div 
                      ref={thumbnailTrackRef}
                      style={{ 
                        display: 'flex',
                        transition: 'transform 0.4s ease-in-out',
                        transform: `translateX(${thumbnailOffset}px)`
                      }}
                    >
                      {images.map((img, index) => (
                        <div 
                          key={index} 
                          ref={el => thumbnailRefs.current[index] = el}
                          style={{ padding: '0 4px' }}
                        >
                          <Image
                            src={img}
                            width={80}
                            height={60}
                            preview={false}
                            onClick={() => handleThumbnailClick(index)}
                            style={{
                              cursor: 'pointer',
                              borderRadius: '4px',
                              border: currentImageIndex === index ? '2px solid #1890ff' : '2px solid #d9d9d9',
                              objectFit: 'cover',
                              imageRendering: 'high-quality' as any
                            }}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </Col>
              </Row>
            )}
          </Card>
        )}

        

        <Row justify="space-between" align="middle">
          <Col style={isMobile ? { marginBottom: '16px' } : {}}>
            <Checkbox checked={skipImageAnalysis} onChange={handleSkipAnalysisChange} disabled={isUpdating || isLoadingStatus}>
              Skip Image Analysis Step (use this for quick estimates and research)
            </Checkbox>
          </Col>
          <Col>
            <Space>
              <Button onClick={onBack}>Back</Button>
              <Button type="primary" onClick={handleSubmit} disabled={uploading || fileList.length > 0}>
                Confirm Images & Continue
              </Button>
            </Space>
          </Col>
        </Row>
      </Space>
      <Modal
        title="Skip Image Analysis"
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        okText="Confirm"
        cancelText="Cancel"
        okButtonProps={{ disabled: true }}
      >
        <p>By checking this box, we will ignore any found images and not analyze them in the PPI report. This is a paid product feature. </p>
      </Modal>
      <Modal
        title="Confirm Deletion"
        open={isDeleteModalVisible}
        onOk={() => imageToDeleteUrl && executeDelete(imageToDeleteUrl)}
        onCancel={() => setIsDeleteModalVisible(false)}
        okText="Delete"
        cancelText="Cancel"
        confirmLoading={deleting}
        okButtonProps={{ danger: true }}
      >
        <p>You can delete irrelavant or unclear images in the full product. This will not affect demo results.</p>
        <Checkbox checked={dontShowDeleteModalAgain} onChange={(e) => {
          const checked = e.target.checked;
          setDontShowDeleteModalAgain(checked);
          localStorage.setItem('dontShowDeleteModalAgain', String(checked));
        }}>
          Don't show this message again
        </Checkbox>
      </Modal>
    </Card>
  );
};